import { Lock, Crown, ShieldAlert, CheckCircle2, ListChecks } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { MARKET_LABELS } from "@/lib/api";
import { useTimezone, fmtEvent, tzAbbr } from "@/lib/time";
import Odds from "@/components/Odds";

function Section({ title, icon: Icon, children, testid }) {
  return (
    <section data-testid={testid}>
      <h4 className="flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.2em] text-[#00E676]"><Icon size={13} /> {title}</h4>
      <div className="mt-2 text-sm leading-relaxed text-slate-300 whitespace-pre-line">{children}</div>
    </section>
  );
}

export function LegList({ legs, locked, compact = false }) {
  if (!legs?.length) return null;
  return (
    <ol className="space-y-2" data-testid="pick-legs">
      {legs.map((l, i) => (
        <li key={`${i}-${l.selection}`} className="rounded-lg border border-[#1E2738] bg-[#0F131D] px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Leg {i + 1} · {MARKET_LABELS[l.market] || l.market}</span>
            <div className="flex items-center gap-2">
              {l.odds != null && <span className="font-mono text-xs text-slate-300"><Odds value={l.odds} /></span>}
              {l.result && l.result !== "pending" && <StatusBadge status={l.result === "void" ? "push" : l.result} />}
            </div>
          </div>
          <p className={`mt-1 text-sm font-semibold ${locked ? "text-slate-500 blur-[3px] select-none" : "text-white"}`}>{l.selection}</p>
          {!compact && !locked && l.analysis && <p className="mt-1 text-xs leading-relaxed text-slate-400">{l.analysis}</p>}
        </li>
      ))}
    </ol>
  );
}

export function PickAnalysisDialog({ pick, open, onOpenChange, onUnlock }) {
  const { tz } = useTimezone();
  const locked = pick.locked;
  const title = pick.pick_type === "multi" ? `${pick.legs?.length || 0}-Leg Combination` : (pick.player ? `${pick.player} — ${pick.selection}` : pick.selection);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#1E2738] bg-[#0A0D14] text-slate-200 sm:max-w-2xl" data-testid={`analysis-dialog-${pick.id}`}>
        <DialogHeader>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{pick.league} · {pick.game}</p>
          <DialogTitle className="font-display text-xl font-bold uppercase tracking-wide text-white">{title}</DialogTitle>
          <p className="font-mono text-xs text-slate-400">{fmtEvent(pick.event_start_utc || pick.game_time, tz)} {tzAbbr(tz, pick.event_start_utc || pick.game_time)} · Odds <Odds value={pick.odds} /> · {pick.units}U</p>
        </DialogHeader>

        {pick.pick_type === "multi" && <LegList legs={pick.legs} locked={locked} />}

        {locked ? (
          <div className="rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-5 text-center" data-testid="analysis-locked">
            <Crown size={22} className="mx-auto text-[#F59E0B]" />
            <p className="mt-2 text-sm text-slate-300">{pick.analysis_preview || "Full research, key factors and risk assessment are available to VIP members."}</p>
            <button onClick={onUnlock} data-testid={`dialog-unlock-${pick.id}`} className="btn-primary mt-4"><Lock size={14} /> Unlock Full Analysis</button>
          </div>
        ) : (
          <div className="space-y-5">
            {(pick.why || pick.analysis) && <Section title="Why We're Recommending This" icon={ListChecks} testid="analysis-why">{pick.why || pick.analysis}</Section>}
            {pick.key_factors?.length > 0 && (
              <Section title="Key Factors" icon={CheckCircle2} testid="analysis-key-factors">
                <ul className="list-disc space-y-1 pl-5">{pick.key_factors.map((k) => <li key={k}>{k}</li>)}</ul>
              </Section>
            )}
            {pick.risks && <Section title="Potential Risks" icon={ShieldAlert} testid="analysis-risks">{pick.risks}</Section>}
            {pick.final_assessment && <Section title="Final Assessment" icon={Crown} testid="analysis-final">{pick.final_assessment}</Section>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
